import React from 'react';
import {TouchableOpacity, View} from 'react-native';
import {ColumnHead} from './ColumnHead';
import {ColumnBodyStyles, ColumnHeadStyles, Props, SortDirection} from './model';
import {useStyles} from '../../hooks/styles';
import {Text} from '../text';
import {LoadableContent} from '../loadableContent';

export default function Table<T extends Record<string, any>>({
  data,
  columns,
  isBordered = true,
  hideBottomLine,
  hideHeaderOnEmpty = false,
  testIdPrefix,
  onRowClick,
}: Props<T>) {
  const dataCopy = data ? [...data] : [];
  const [sortField, setSortField] = React.useState<string | null>(null);
  const [sortOrder, setSortOrder] = React.useState<SortDirection>('none');
  const showHeader = hideHeaderOnEmpty ? dataCopy.length > 0 : true;

  const styles = useStyles(({theme}) => ({
    row: {
      borderBottomWidth: isBordered ? 1 : 0,
      borderStyle: 'solid',
      borderColor: theme.color.borderColor,
      flexDirection: 'row',
    },
    colBody: {
      paddingTop: 8,
      paddingBottom: 8,
      minHeight: 59,
      justifyContent: 'center',
    },
    colBodyText: {
      ...theme.fonts.bodySmallLight,
      color: theme.color.labelText,
    },
  }));

  const sorter = (a: T, b: T) => {
    if (sortField == null) return 0;

    const fieldA = a[sortField];
    const fieldB = b[sortField];

    if (sortOrder === 'none' || fieldA === fieldB) return 0;
    if (sortOrder === 'asc') return fieldA > fieldB ? 1 : -1;
    if (sortOrder === 'desc') return fieldA > fieldB ? -1 : 1;
    return 0;
  };

  return (
    <View>
      {showHeader && (
        <View style={[styles.row]}>
          {columns.map((column, i) => {
            const style: ColumnHeadStyles = column.headStyles ?? {};
            const {colHeadStyle = null, colHeadTextStyle = null} = style;
            return (
              <ColumnHead
                key={i}
                column={column}
                colHeadStyle={colHeadStyle}
                colHeadTextStyle={colHeadTextStyle}
                activeSortOrder={column.field === sortField ? sortOrder : 'none'}
                onColumnSortOrder={(order) => {
                  setSortField(column.field);
                  setSortOrder(order);
                }}
              />
            );
          })}
        </View>
      )}
      <LoadableContent loading={!data}>
        {dataCopy?.sort(sorter).map((item, i) => {
          const testid = item.id && testIdPrefix ? {testID: `${testIdPrefix}-${item.id}`} : {};
          return (
            <View key={item.id ?? i} {...testid}>
              <TouchableOpacity onPress={() => onRowClick?.(item)} disabled={!onRowClick}>
                <View style={[styles.row, hideBottomLine && i === dataCopy.length - 1 && {borderBottomWidth: 0}]}>
                  {columns.map((column, j) => {
                    const style: ColumnBodyStyles =
                      typeof column.styles === 'function' ? column.styles(item) : column.styles ?? {};
                    const {colBodyStyle = null, colBodyTextStyle = null} = style;
                    return (
                      <View style={[styles.colBody, colBodyStyle]} key={j}>
                        {column.render ? (
                          column.render(item)
                        ) : (
                          <Text style={[styles.colBodyText, colBodyTextStyle]}>{item[column.field] ?? '–'}</Text>
                        )}
                      </View>
                    );
                  })}
                </View>
              </TouchableOpacity>
            </View>
          );
        })}
      </LoadableContent>
    </View>
  );
}
